/**
 * Presentation helpers for the weekend-shift admin screens. Business rules
 * (caps, availability, check-in windows) come from the API; these only
 * label and group what it returns.
 */
import type { StatusTone } from '../../../theme/sbgTokens';
import { addDaysToYmd } from '../../../utils/easternTime';
import { MISSING, formatCount, formatDate, formatDateRange, formatDateTime, formatNumber, formatTime } from '../../../utils/format';
import type { WeekendHolidayDay, WeekendShiftCheckpointDetail, WeekendShiftRosterRow } from './types';

// Holiday shifts (Friday or Monday) are uncapped extras that do not count
// toward the per-rep Sat/Sun cap.
export const isUncappedHolidayShiftKey = (key: string) =>
  key === 'fri_am' || key === 'fri_pm' || key === 'mon_am' || key === 'mon_pm';

export const getHolidayDayLabel = (holidayDay: WeekendHolidayDay | null | undefined) =>
  holidayDay === 'friday' ? 'Friday' : 'Monday';

export const hasMissedStart = (rep: WeekendShiftRosterRow) =>
  rep.selectedShiftDetails.some((detail) => detail.startCheckin.checkinStatus === 'window_closed');

export const hasMissedEnd = (rep: WeekendShiftRosterRow) =>
  rep.selectedShiftDetails.some((detail) => detail.endCheckin.checkinStatus === 'window_closed');

export const hasMissedAny = (rep: WeekendShiftRosterRow) => hasMissedStart(rep) || hasMissedEnd(rep);

/** "Sat 9-1" → "Sat 9–1"; "9:00 AM - 1:00 PM" → "9:00 AM – 1:00 PM" (en dash for ranges, book §13.1). */
export const tidyRange = (label: string) =>
  label.replace(/\s+-\s+/g, ' – ').replace(/(\d)-(\d)/g, '$1–$2');

/**
 * "Sat–Sun, Oct 10–11, 2026", or "Sat–Mon, Oct 10–12, 2026" on a Monday
 * holiday weekend. `weekendStartDate` is the Saturday (Eastern YYYY-MM-DD).
 */
export const formatWeekendRange = (weekendStartDate: string, holidayDay?: WeekendHolidayDay | null) => {
  if (!weekendStartDate) return MISSING;
  const from = holidayDay === 'friday' ? addDaysToYmd(weekendStartDate, -1) : weekendStartDate;
  const to = holidayDay === 'monday' ? addDaysToYmd(weekendStartDate, 2) : addDaysToYmd(weekendStartDate, 1);
  const days = holidayDay === 'friday' ? 'Fri–Sun' : holidayDay === 'monday' ? 'Sat–Mon' : 'Sat–Sun';
  return `${days}, ${formatDateRange(from, to)}`;
};

/**
 * The analytics endpoint names holiday weekends in its long label
 * ("…, and Monday, October 12, 2026"); read the holiday day back from it so
 * the short range covers the extra day.
 */
export const holidayDayFromLabel = (label: string | null | undefined): WeekendHolidayDay | null => {
  if (!label) return null;
  if (/\bMonday\b/.test(label)) return 'monday';
  if (/\bFriday\b/.test(label)) return 'friday';
  return null;
};

/** "Aug 15 – Oct 4, 2026" for a run of weekends (first Saturday to last Sunday). */
export const formatWeekendSpan = (startWeekendDate: string, endWeekendDate: string) =>
  formatDateRange(startWeekendDate, addDaysToYmd(endWeekendDate, 1));

/** Number of weekends from start to end, inclusive (both are Saturdays). */
export const countWeekends = (startWeekendDate: string, endWeekendDate: string) => {
  const start = new Date(`${startWeekendDate}T12:00:00Z`).getTime();
  const end = new Date(`${endWeekendDate}T12:00:00Z`).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return 0;
  return Math.round((end - start) / (7 * 24 * 60 * 60 * 1000)) + 1;
};

const phaseWord = (phase: 'start' | 'end') => (phase === 'start' ? 'Start' : 'End');

/**
 * How one check-in checkpoint reads. Status color is kept for exceptions:
 * missed and offsite (danger; both count toward a suspension) and a live
 * window (warning) are pills. Confirmed and not-yet-open checkpoints are
 * quiet text, so a normal weekend doesn't fill the roster with color.
 */
export type CheckpointView =
  | { kind: 'pill'; tone: StatusTone; label: string; time: string | null }
  | { kind: 'confirmed'; label: string; time: string | null }
  | { kind: 'pending'; label: string };

export const checkpointView = (
  phase: 'start' | 'end',
  checkpoint: WeekendShiftCheckpointDetail
): CheckpointView => {
  const word = phaseWord(phase);
  switch (checkpoint.checkinStatus) {
    case 'confirmed':
      return { kind: 'confirmed', label: word, time: checkpoint.confirmedAt ? formatTime(checkpoint.confirmedAt) : null };
    case 'offsite_click':
      return {
        kind: 'pill',
        tone: 'danger',
        label: `${word} offsite`,
        time: checkpoint.latestCheckinClickedAt ? formatTime(checkpoint.latestCheckinClickedAt) : null,
      };
    case 'awaiting_checkin':
      return { kind: 'pill', tone: 'warning', label: `Awaiting ${phase}`, time: null };
    case 'window_closed':
      return { kind: 'pill', tone: 'danger', label: `${word} missed`, time: null };
    default:
      return { kind: 'pending', label: `${word} not open yet` };
  }
};

/** The detail line under a checkpoint in the rep sheet: when it landed or was tried, else the API's reason. */
export const checkpointMeta = (checkpoint: WeekendShiftCheckpointDetail) => {
  if (checkpoint.confirmedAt) return formatDateTime(checkpoint.confirmedAt);
  if (checkpoint.latestCheckinClickedAt) return `Last attempt ${formatDateTime(checkpoint.latestCheckinClickedAt)}`;
  return checkpoint.checkinStatusReason;
};

/** "Nov 12, 2026" — the last day of a suspension (inclusive). */
export const suspensionEndDate = (rep: WeekendShiftRosterRow) =>
  rep.suspensionStatus?.suspendedUntil
    ? formatDate(rep.suspensionStatus.suspendedUntil)
    : rep.suspensionStatus?.endsOnLabel || MISSING;

/** "Nov 12" this year; "Jan 5, 2027" when the suspension runs into another year. */
const suspensionEndShort = (suspendedUntil: string) => {
  const sameYear = formatDate(suspendedUntil).slice(-4) === formatDate(new Date()).slice(-4);
  return formatDate(suspendedUntil, { short: sameYear });
};

const allCheckpoints = (rep: WeekendShiftRosterRow) =>
  rep.selectedShiftDetails.flatMap((detail) => [detail.startCheckin, detail.endCheckin]);

/** Check-ins whose window has opened (anything not still "scheduled"). */
export const countDueCheckpoints = (rep: WeekendShiftRosterRow) =>
  allCheckpoints(rep).filter((checkpoint) => checkpoint.checkinStatus !== 'scheduled').length;

/**
 * A rep's state for the selected weekend. Exceptions that the shift lines
 * don't already show (suspended, over the cap) are pills; otherwise one
 * quiet summary. Missed check-ins show on the shift line itself.
 */
export const repStatus = (rep: WeekendShiftRosterRow, overCap: boolean) => {
  const pills: { tone: StatusTone; label: string }[] = [];
  if (rep.suspensionStatus?.isSuspended) {
    pills.push({
      tone: 'danger',
      label: rep.suspensionStatus.suspendedUntil
        ? `Suspended through ${suspensionEndShort(rep.suspensionStatus.suspendedUntil)}`
        : 'Suspended',
    });
  }
  if (overCap) pills.push({ tone: 'warning', label: 'Over cap' });

  let summary: string;
  const checkpoints = allCheckpoints(rep);
  const confirmed = checkpoints.filter((checkpoint) => checkpoint.checkinStatus === 'confirmed').length;
  if (rep.shiftCount === 0 && checkpoints.length === 0) summary = 'No shifts';
  else if (checkpoints.length === 0 || countDueCheckpoints(rep) === 0) summary = 'Signed up';
  else if (confirmed === checkpoints.length) summary = 'All checked in';
  else summary = `${formatNumber(confirmed)} of ${formatCount(checkpoints.length, 'check-in')} confirmed`;

  return { pills, summary };
};

/** True when the rep needs a look: suspended, missed or offsite, or over the cap. */
export const repNeedsAttention = (rep: WeekendShiftRosterRow, overCap: boolean) =>
  Boolean(rep.suspensionStatus?.isSuspended)
  || overCap
  || rep.selectedShiftDetails.some((detail) =>
    [detail.startCheckin, detail.endCheckin].some(
      (checkpoint) => checkpoint.checkinStatus === 'window_closed' || checkpoint.checkinStatus === 'offsite_click'
    )
  );

/** Ends an API message with a period so it can sit inside a longer sentence. */
export const asSentence = (text: string) => {
  const trimmed = text.trim();
  return /[.?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

export const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;

/** "Sat 9–1, Sun 1–5" for a list of shift keys. */
export const shiftListLabel = (keys: string[], labelsByKey: Map<string, string>) =>
  keys.map((key) => tidyRange(labelsByKey.get(key) ?? key)).join(', ');
